import { inject, Service } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import type { ProductDto, ProductReviewDto } from '@api/models';
import { WEBSITE_ORIGIN } from '../constants';
import { resolveActivePriceIndex } from '../helpers/resolve-active-price-index.function';

/** A schema.org document: nested plain JSON, shaped by whichever @type it declares. */
export type JsonLd = { [key: string]: unknown };

/** The Product document below is assembled field by field, so `offers` has to stay reachable. */
interface ProductJsonLd extends JsonLd {
  offers: {
    price?: number;
    priceSpecification?: unknown[];
    [key: string]: unknown;
  };
}

@Service()
export class JsonLdService {
  private sanitizer = inject(DomSanitizer);


  getSafeJsonLd(jsonLD: JsonLd): SafeHtml {
    const json = JSON.stringify(jsonLD, null, 2).replace(/<\/script>/g, '<\\/script>'); // escape / to prevent script tag in JSON
    const html = `<script type="application/ld+json">${json}</script>`;

    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  getSafeJsonLdForProduct(product: ProductDto, reviews: ProductReviewDto[]): SafeHtml {
    const jsonLd: ProductJsonLd = {
      '@context': 'http://schema.org',
      '@type': 'Product',
      itemCondition: 'https://schema.org/NewCondition',
      description: product.fullDescription,
      name: product.name,
      offers: {
        '@type': 'Offer',
        availability: product.isInStock ? 'http://schema.org/InStock' : 'http://schema.org/OutOfStock',
        priceCurrency: 'UAH',
        priceValidUntil: '2040-12-08',
        url: `${WEBSITE_ORIGIN}/${product.slug}`
      },
      url: `${WEBSITE_ORIGIN}/${product.slug}`
    };
    // No cart of its own to go by: the price a single one of these sells at.
    const activePriceIndex = resolveActivePriceIndex(product, 0);
    const price = activePriceIndex === undefined
      ? Math.round(product.price)
      : Math.round(product.discountedPrices[activePriceIndex].price);

    if (product.discountedPrices && product.discountedPrices.length !== 0) {
      jsonLd.offers.priceSpecification = [];
      for (const dp of product.discountedPrices) {
        jsonLd.offers.priceSpecification.push({
          '@type': 'PriceSpecification',
          price: dp.price,
          eligibleTransactionVolume: {
            '@type': 'PriceSpecification',
            price: dp.costBreakpoint
          }
        });
      }
    }
    jsonLd.offers.price = price;
    if (product.vendorCode) {
      jsonLd.sku = product.vendorCode;
      jsonLd.identifier = product.sku;
    } else {
      jsonLd.sku = product.sku;
    }

    if (product.gtin) {
      jsonLd.gtin = product.gtin;
    }

    const manufacturer = product.characteristics.find(c => c.code === 'manufacturer');
    if (manufacturer) {
      jsonLd.brand = manufacturer;
      jsonLd.manufacturer = manufacturer;
    }

    if (product.medias[0]) {
      jsonLd.image = `${WEBSITE_ORIGIN}${product.medias[0].variantsUrls.original}`;
    }

    const ratings: JsonLd = {};
    if (product.reviewsInfo.textReviewsCount > 0) {
      ratings.aggregateRating = {
        '@type': 'AggregateRating',
        ratingValue: product.reviewsInfo.avgRating,
        reviewCount: product.reviewsInfo.allReviewsCount,
      };

      ratings.review = reviews.map(review => ({
        '@type': 'Review',
        author: review.name,
        datePublished: review.createdAt,
        description: review.text,
        "reviewRating": {
          "@type": "Rating",
          "bestRating": "5",
          "ratingValue": review.rating,
          "worstRating": "1"
        }
      }));
    }

    if (!product.productGroup) {
      return this.getSafeJsonLd({ ...jsonLd, ...ratings });
    }

    return this.getSafeJsonLd(this.buildProductGroupJsonLd(product, jsonLd, ratings));
  }

  /**
   * Google's multi-page variant markup: the page's own variant in full inside a ProductGroup, every
   * other variant by URL only - its own page describes it. The rating and the reviews are pooled over
   * the group, so they sit on the group. `productGroupID` matches the Merchant feed's item_group_id.
   */
  private buildProductGroupJsonLd(product: ProductDto, variantJsonLd: ProductJsonLd, ratings: JsonLd): JsonLd {
    const { productGroupId, variesBy, variantSlugs } = product.productGroup;
    const { '@context': context, ...variant } = variantJsonLd;

    return {
      '@context': context,
      '@type': 'ProductGroup',
      name: product.name,
      productGroupID: productGroupId,
      variesBy: variesBy.map(attribute => `https://schema.org/${attribute}`),
      ...(variant.brand ? { brand: variant.brand } : {}),
      ...ratings,
      hasVariant: variantSlugs.map(slug => slug === product.slug
        ? { ...variant, inProductGroupWithID: productGroupId }
        : { '@type': 'Product', url: `${WEBSITE_ORIGIN}/${slug}` }),
    };
  }
}
